import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import ProductSEO from '../../_Sections/ProductSEO';
import SectionBranding from '../../_Sections/SectionBranding';
import SectionDetails from '../../_Sections/SectionDetails';
import SectionPairings from '../../_Sections/SectionPairings';
import SectionPricing from '../../_Sections/SectionPricing';
import SectionSizes from '../../_Sections/SectionSizes';
import SectionGallery from '../../_Sections/SectionGallery';
import PrintSizeModule, { type PrintOrientation, type PrintSize } from '../../_Sections/PrintSizeModule';
import icon1Url from '@resources/img/pages/products/aluminum-prints/icon-1.svg?url';
import icon2Url from '@resources/img/pages/products/aluminum-prints/icon-2.svg?url';
import icon3Url from '@resources/img/pages/products/aluminum-prints/icon-3.svg?url';
import icon4Url from '@resources/img/pages/products/aluminum-prints/icon-4.svg?url';
import icon5Url from '@resources/img/pages/products/aluminum-prints/icon-5.svg?url';

const PRODUCT_ID = 136;
const VIEW_PRODUCT_ID = 166;

const bannerImages = {
  en_CA: new URL('@resources/img/pages/products/aluminum-prints/banner-1-en_CA.png', import.meta.url).href,
  fr_CA: new URL('@resources/img/pages/products/aluminum-prints/banner-1-fr_CA.png', import.meta.url).href,
};

const galleryImages = [
  new URL('@resources/img/pages/products/aluminum-prints/gallery_0.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/aluminum-prints/gallery_1.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/aluminum-prints/gallery_2.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/aluminum-prints/gallery_3.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/aluminum-prints/gallery_4.jpg', import.meta.url).href,
];

const squareSizes: PrintSize[] = [
  { width: 12, height: 12, tone: 0 },
  { width: 16, height: 16, tone: 1 },
];

const orientedSizes: Record<PrintOrientation, PrintSize[]> = {
  landscape: [
    { width: 14, height: 11, tone: 2 },
    { width: 16, height: 12, tone: 3 },
    { width: 18, height: 12, tone: 4 },
  ],
  portrait: [
    { width: 11, height: 14, tone: 2 },
    { width: 12, height: 16, tone: 3 },
    { width: 12, height: 18, tone: 4 },
  ],
};

export default function AluminumPrintPage() {
  const [t] = useTranslation('page/products/aluminum-prints');
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(PRODUCT_ID)));
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'));
  const bannerImage = locale === 'fr_CA' ? bannerImages.fr_CA : bannerImages.en_CA;

  if (!productData) return null;

  return <div className="centered-page product-page-v4">
    <ProductSEO
      viewProductId={VIEW_PRODUCT_ID}
      title={t('Custom sublimation Aluminum Prints | Products')}
      description={t('Design and create custom Aluminum Prints featuring your artwork. Print-on-demand drop shipping, no minimums, and made in Canada.')}
    />
    <SectionDetails
      viewProductId={VIEW_PRODUCT_ID}
      color="#d9e3e6"
      bgStyle={{
        backgroundPosition: 'center right',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '50%',
      }}
      tag={t('The all-over print')}
      title={t('Aluminum Print')}
      text={t('Immortalize your artwork as custom aluminum prints. Printed on-demand on high gloss aluminum, these vibrant metal prints deliver sharp detail, rich colour, and a luminous finish. Lightweight yet durable, aluminum prints are ideal for photography, digital art, and illustrations.')}
      createUrl={productData.get('url_slug')}
      img={bannerImage}
      alt={t('Picture of Aluminum Print')}
      details={[
        {
          img: icon1Url,
          alt: t('Cotton icon'),
          text: t('High gloss coated aluminum surface '),
        },
        {
          img: icon2Url,
          alt: t('Print icon'),
          text: t('Vibrant, high-definition colour reproduction '),
        },
        {
          img: icon3Url,
          alt: t('Canvas icon'),
          text: t('Wide mounting block for easy hanging'),
        },
        {
          img: icon4Url,
          alt: t('Hardware icon'),
          text: t('Durable and vibrant sublimation print'),
        },
        {
          img: icon5Url,
          alt: t('Canada icon'),
          text: t('Digitally printed in Montreal, Canada and shipped worldwide'),
        },
      ]}
    />

    <SectionGallery images={galleryImages} />

    <SectionPricing
      productId={PRODUCT_ID}
      variants={['11x14', '14x11', '12x16', '16x12', '12x18', '18x12', '16x16']}
      listOptions
    />

    <SectionSizes
      productId={PRODUCT_ID}
      title={t('Aluminum Print Sizing')}
      text={t('Choose from square, portrait, and landscape formats.')}
    >
      <PrintSizeModule
        squareSizes={squareSizes}
        orientedSizes={orientedSizes}
      />
    </SectionSizes>

    <SectionBranding
      title={t('Brand your Aluminum Print')}
      text={t('Branding enhances your products. Add custom artist cards or stickers to your packages to create a cohesive message and build your brand.')}
    />

    <SectionPairings
      text={t('Pair your Aluminum Print with these wall-art products to create your own unique styles.')}
      productSlugs={['art-prints', 'digital-prints', 'tapestries']}
    />
  </div>
}
